import { useEffect, useRef } from "react";

// Minimal constructor type for the Web Speech API, covering both the
// unprefixed (Chrome/Edge) and webkit-prefixed (older Safari) variants.
// We avoid referencing the global `SpeechRecognition` name directly to stay
// compatible with TypeScript DOM lib versions that expose it inconsistently.
interface SpeechRecognitionConstructor {
  new(): SpeechRecognitionInstance;
}

interface SpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onresult: ((event: SpeechRecognitionResultEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrEvent) => void) | null;
}

interface SpeechRecognitionResultEvent {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrEvent {
  error: string;
}

export type UseWakeWordParams = {
  /** Keywords matched as case-insensitive substrings in the recognised transcript. */
  keywords: string[];
  /** BCP-47 language tag, e.g. "en-US" or "ar-SA". */
  lang: string;
  /**
   * Master switch. When true the listener runs (or restarts after silence).
   * When false the listener is stopped and will not restart.
   */
  enabled: boolean;
  /** Called once when any keyword is matched. The listener stops itself before invoking this. */
  onKeywordDetected: () => void;
};

/**
 * Runs a continuous, lightweight background SpeechRecognition listener.
 * When any keyword is detected it calls `onKeywordDetected` and suspends
 * itself. The listener auto-restarts whenever the browser stops it due to
 * silence, as long as `enabled` remains true.
 *
 * Falls back to a graceful no-op when the Web Speech API is not available
 * (e.g. Firefox without the flag, non-Chromium environments).
 */
export const useWakeWord = ({
  keywords,
  lang,
  enabled,
  onKeywordDetected,
}: UseWakeWordParams): void => {
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  // keywords and callback are read by async closures — always keep them current.
  const keywordsRef = useRef(keywords);
  const onKeywordDetectedRef = useRef(onKeywordDetected);
  const matchedRef = useRef(false); // prevents double-firing within one session
  // Prevents scheduling multiple simultaneous restarts (e.g. onerror + onend
  // both firing, or visibilitychange racing with onend).
  const restartPendingRef = useRef(false);
  // enabledRef is intentionally NOT updated in the render body.
  // It is written at the start of the effect (after the previous cleanup has run)
  // so that the cleanup's `enabledRef.current = false` cannot silently prevent
  // the next run of startListening() from starting.
  const enabledRef = useRef(enabled);

  keywordsRef.current = keywords;
  onKeywordDetectedRef.current = onKeywordDetected;

  useEffect(() => {
    // Must be the very first line — this runs after the previous cleanup, so it
    // correctly reflects the current `enabled` value before startListening() fires.
    enabledRef.current = enabled;
    const w = window as Window & {
      SpeechRecognition?: SpeechRecognitionConstructor;
      webkitSpeechRecognition?: SpeechRecognitionConstructor;
    };
    const SpeechRecognitionAPI = w.SpeechRecognition ?? w.webkitSpeechRecognition;

    if (!SpeechRecognitionAPI) {
      // Web Speech API unavailable — silent no-op
      return;
    }

    const scheduleRestart = (delayMs = 300) => {
      if (restartPendingRef.current) return; // already scheduled
      restartPendingRef.current = true;
      setTimeout(() => {
        restartPendingRef.current = false;
        startListening();
      }, delayMs);
    };

    const startListening = () => {
      console.log("wake-start-listening", enabledRef.current);
      if (!enabledRef.current) return;
      // Don't create a second instance if one is already active.
      if (recognitionRef.current) return;

      const recognition = new SpeechRecognitionAPI();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = lang;
      matchedRef.current = false;
      recognitionRef.current = recognition;

      recognition.onresult = (event: SpeechRecognitionResultEvent) => {
        console.log("wake-onresult", event);
        if (matchedRef.current) return;

        let fullTranscript = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          fullTranscript += event.results[i][0].transcript;
        }
        const lower = fullTranscript.toLowerCase();

        console.log("fullTranscript", fullTranscript, lower);
        const hit = keywordsRef.current.some((kw) =>
          lower.includes(kw.toLowerCase())
        );

        if (hit) {
          matchedRef.current = true;
          recognition.stop();
          onKeywordDetectedRef.current();
        }
      };

      recognition.onend = () => {
        recognitionRef.current = null;
        // Auto-restart if still enabled and no keyword was matched (i.e. stopped
        // due to silence, not because we triggered it).
        if (enabledRef.current && !matchedRef.current) {
          scheduleRestart(300);
        }
      };

      recognition.onerror = (event: SpeechRecognitionErrEvent) => {
        if (
          event.error === "not-allowed" ||
          event.error === "service-not-allowed"
        ) {
          // Fatal — do not retry.
          enabledRef.current = false;
          return;
        }

        // For all other errors (aborted, no-speech, audio-capture, network),
        // schedule a restart immediately. Chrome sometimes fires onerror
        // WITHOUT a subsequent onend (especially when the page is idle or
        // backgrounded), which would permanently kill the listener if we only
        // restart inside onend. Using a slightly longer delay here avoids a
        // tight loop when Chrome keeps aborting due to the page being inactive.
        if (enabledRef.current && !matchedRef.current) {
          recognitionRef.current = null;
          scheduleRestart(event.error === "aborted" ? 800 : 300);
        }
      };

      try {
        recognition.start();
      } catch {
        // start() can throw if already started — safe to ignore.
        recognitionRef.current = null;
      }
    };

    // Resume the listener when the page becomes visible again after being
    // backgrounded or the screen turning off.
    const handleVisibilityChange = () => {
      if (
        !document.hidden &&
        enabledRef.current &&
        !recognitionRef.current
      ) {
        scheduleRestart(300);
      }
    };

    if (enabled) {
      document.addEventListener("visibilitychange", handleVisibilityChange);
      startListening();
    } else {
      // Stop any running instance immediately.
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch {
          // ignore
        }
        recognitionRef.current = null;
      }
    }

    return () => {
      // Cleanup: stop listening without restarting.
      enabledRef.current = false;
      restartPendingRef.current = false;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch {
          // ignore
        }
        recognitionRef.current = null;
      }
    };
    // Re-run only when enabled or lang changes. Keywords and callbacks are
    // always read through refs so they don't need to be dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, lang]);
};
